import {
  MarkedCellType,
  type MarkedCell,
  type Tutorial,
  type TutorialDemo,
  type TutorialStep,
  type CellState,
} from '@puzzlio/core';
import { meta } from './config';
import { generateTutorialHint } from './logic/tutorialHintsGenerator';
import { cellValue, type StarsBoard } from './types';

type Coord = [row: number, col: number];

const STAR = 2;
const X = 1;

const mark = ([row, col]: Coord, markType: MarkedCellType): MarkedCell => ({ row, col, markType });
const wantStar = (coords: Coord[]) => coords.map((c) => mark(c, MarkedCellType.hintFill));
const wantX = (coords: Coord[]) => coords.map((c) => mark(c, MarkedCellType.hintCross));
const reference = (coords: Coord[]) => coords.map((c) => mark(c, MarkedCellType.reference));

const allCells = (board: StarsBoard): Coord[] => board.regions.flatMap((row, r) => row.map((_, c): Coord => [r, c]));
const valuesAre = (cells: CellState[][], coords: Coord[], value: number) =>
  coords.every(([r, c]) => cellValue(cells[r]?.[c] ?? 0) === value);
/** The solution's stars among the given cells — what a rule points at. */
const starsAmong = (board: StarsBoard, coords: Coord[]) => coords.filter(([r, c]) => board.solution[r][c] === STAR);

/** The cells touching any of `coords`, diagonals included, and not themselves in `coords`. */
const neighbours = (board: StarsBoard, coords: Coord[]): Coord[] => {
  const size = board.regions.length;
  const own = new Set(coords.map(([r, c]) => `${r},${c}`));
  const found = new Map<string, Coord>();
  for (const [r, c] of coords) {
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        const [nr, nc] = [r + dr, c + dc];
        const key = `${nr},${nc}`;
        if (nr >= 0 && nr < size && nc >= 0 && nc < size && !own.has(key)) found.set(key, [nr, nc]);
      }
    }
  }
  return [...found.values()];
};

/** Lock every cell but `enabled`. */
const enableOnly = (board: StarsBoard, enabled: Coord[]): MarkedCell[] => {
  const keep = new Set(enabled.map(([r, c]) => `${r},${c}`));
  return allCells(board)
    .filter(([r, c]) => !keep.has(`${r},${c}`))
    .map((c) => mark(c, MarkedCellType.disabled));
};

/**
 * A rule, stated and pointed at. Nothing to do on the board but look, so a rule may hand the board's
 * place to a demo instead and leave its marks unused.
 */
const rule =
  (
    heading: string,
    text: string,
    marks: (board: StarsBoard) => MarkedCell[],
    { showReset = true, demo }: { showReset?: boolean; demo?: (board: StarsBoard) => StarsDemo } = {}
  ) =>
  ({ board }: { board: StarsBoard }): TutorialStep => ({
    message: { kind: 'instruction', heading, blocks: [text], done: true, showReset },
    marks: marks(board),
    canInteract: false,
    demo: demo?.(board),
  });

/**
 * A practice step: the player places `value` in every target, and nothing else is in play. The task
 * is a sentence of its own rather than a checklist item; Next waiting on it is the only tick it gets.
 */
const practice =
  (text: string, task: string, value: number, targets: (board: StarsBoard) => Coord[]) =>
  ({ board, cells }: { board: StarsBoard; cells: CellState[][] }): TutorialStep => {
    const coords = targets(board);
    const done = valuesAre(cells, coords, value);
    return {
      message: { kind: 'instruction', blocks: [text, task], done },
      marks: [...enableOnly(board, coords), ...(value === STAR ? wantStar(coords) : wantX(coords))],
      canInteract: !done,
    };
  };

/** Whether any cell a step's hint named still doesn't hold its mark. */
const hintUnfinished = (step: TutorialStep, cells: CellState[][]) =>
  (step.marks ?? []).some(
    ({ row, col, markType }) =>
      (markType === MarkedCellType.hintFill && cellValue(cells[row][col]) !== STAR) ||
      (markType === MarkedCellType.hintCross && cellValue(cells[row][col]) !== X)
  );

/**
 * The marks on the board when each hint was given: what its reasoning rests on. Keyed by the step
 * itself, which comes back as `previous` while the player works through it.
 */
const hintEvidence = new WeakMap<TutorialStep, [row: number, col: number, value: number][]>();

const marksOn = (cells: CellState[][]) =>
  cells.flatMap((row, r) =>
    row.flatMap((cell, c): [number, number, number][] => (cellValue(cell) === 0 ? [] : [[r, c, cellValue(cell)]]))
  );

/** Whether every mark a hint was reasoning from is still there. Marks added since don't matter. */
const evidenceIntact = (step: TutorialStep, cells: CellState[][]) =>
  hintEvidence.get(step)?.every(([r, c, value]) => cellValue(cells[r][c]) === value) ?? false;

/**
 * Cells holding a mark the solution disagrees with. An empty cell is never wrong, only unfinished — and
 * neither is an ✕ where the hint on screen asks for a star, since a star takes two taps and the ✕ is
 * the first of them.
 */
const mistakes = (board: StarsBoard, cells: CellState[][], hint?: TutorialStep): Coord[] => {
  const starsOnTheWay = new Set(
    (hint?.marks ?? []).filter((m) => m.markType === MarkedCellType.hintFill).map((m) => `${m.row},${m.col}`)
  );
  return allCells(board).filter(([r, c]) => {
    const value = cellValue(cells[r][c]);
    if (value === STAR) return board.solution[r][c] !== STAR;
    return value === X && board.solution[r][c] === STAR && !starsOnTheWay.has(`${r},${c}`);
  });
};

/*
  Written against the `tutorial-1` board, 9×9. The rules point at the solution's own stars, so
  nothing they show is ever contradicted later. The practice steps use the region in the bottom-left
  corner, three cells in a column: its two stars can't touch, so they have to sit at either end — every
  rule at once, in the first move of the puzzle.
*/
const EXAMPLE_ROW = 0;
const EXAMPLE_COL = 8;
/** Near the middle and clear of the example row and column, so the neighbourhood reads on its own. */
const EXAMPLE_STAR: Coord = [4, 5];
const SHORT_REGION_ENDS: Coord[] = [
  [4, 0],
  [6, 0],
];

const inRow = (board: StarsBoard, r: number) => board.regions[r].map((_, c): Coord => [r, c]);
const inCol = (board: StarsBoard, c: number) => board.regions.map((_, r): Coord => [r, c]);

export type StarsDemo = TutorialDemo<Pick<StarsBoard, 'regions'>, CellState>;

/**
 * One demo per board, however often the step is evaluated: a new demo object would start the loop
 * over.
 */
const perBoard = (build: (board: StarsBoard) => StarsDemo) => {
  const built = new WeakMap<StarsBoard, StarsDemo>();
  return (board: StarsBoard) => {
    if (!built.has(board)) built.set(board, build(board));
    return built.get(board)!;
  };
};

/*
  Rule 1 acted out on the tutorial's own board, solved: the board goes faint, then a row, a column
  and a region light up in turn, each with its two stars back at full strength, before the whole
  solution comes back. All three run through the star at (6,4).
*/
const RULE_1_STAR: Coord = [6, 4];

const RULE_1_DEMO = perBoard((board) => {
  const solved = board.solution.map((row) => row.map((value) => (value === STAR ? STAR : X)));
  const [r, c] = RULE_1_STAR;
  const region = allCells(board).filter(([rr, cc]) => board.regions[rr][cc] === board.regions[r][c]);
  const faintExcept = (lit: Coord[]) =>
    allCells(board)
      .filter(([rr, cc]) => !lit.some(([lr, lc]) => lr === rr && lc === cc))
      .map((cell) => mark(cell, MarkedCellType.faded));
  const faint = faintExcept([]);

  return {
    board,
    alt: 'Two stars in each row, each column and each region',
    frames: [
      { cells: solved, hold: 1200 },
      { cells: solved, marks: faint, hold: 500 },
      ...[inRow(board, r), inCol(board, c), region].flatMap((group) => [
        { cells: solved, marks: [...reference(group), ...faintExcept(starsAmong(board, group))], hold: 1300 },
        { cells: solved, marks: faint, hold: 350 },
      ]),
    ],
  };
});

/*
  Rule 2 acted out on the tutorial's own board, empty but for the example star: a second star placed
  touching it diagonally, the clash shown in red, the second taken back, and the neighbours crossed off
  one by one, starting where the clashing star was and going clockwise.
*/
const RULE_2_DEMO = perBoard((board) => {
  const [r, c] = EXAMPLE_STAR;
  const clockwise: Coord[] = [
    [1, 1],
    [1, 0],
    [1, -1],
    [0, -1],
    [-1, -1],
    [-1, 0],
    [-1, 1],
    [0, 1],
  ];
  const ring = clockwise.map(([dr, dc]): Coord => [r + dr, c + dc]);
  const [clash] = ring;
  const cellsWith = (stars: Coord[], xs: Coord[] = []) =>
    board.regions.map((row, rr) =>
      row.map((_, cc) => {
        const at = ([ar, ac]: Coord) => ar === rr && ac === cc;
        return stars.some(at) ? STAR : xs.some(at) ? X : 0;
      })
    );

  return {
    alt: 'Stars cannot touch each other, not even diagonally',
    board,
    frames: [
      { cells: cellsWith([EXAMPLE_STAR]), hold: 800 },
      { cells: cellsWith([EXAMPLE_STAR, clash]), hold: 700 },
      {
        cells: cellsWith([EXAMPLE_STAR, clash]),
        marks: ring.map((cell) => mark(cell, MarkedCellType.error)),
        hold: 1000,
      },
      { cells: cellsWith([EXAMPLE_STAR]), hold: 500 },
      ...ring.map((_, i) => ({ cells: cellsWith([EXAMPLE_STAR], ring.slice(0, i + 1)), hold: 80 })),
      { cells: cellsWith([EXAMPLE_STAR], ring), hold: 1800 },
    ],
  };
});

export const tutorial: Tutorial<StarsBoard, CellState> = {
  game: 'stars',
  phases: [{ slug: meta.tutorialSlug, firstStep: 0 }],
  steps: [
    rule('Welcome to Stars!', 'Your goal is to place stars on the board, following two simple rules.', () => [], {
      showReset: false,
    }),

    rule(
      'Rule 1',
      'Every row, every column, and every region needs exactly two stars.',
      (board) => {
        const lines = [...inRow(board, EXAMPLE_ROW), ...inCol(board, EXAMPLE_COL)];
        return [...reference(lines), ...wantStar(starsAmong(board, lines))];
      },
      { demo: RULE_1_DEMO }
    ),

    rule(
      'Rule 2',
      "Stars can't touch each other, not even diagonally.",
      (board) => [...wantStar([EXAMPLE_STAR]), ...wantX(neighbours(board, [EXAMPLE_STAR]))],
      { demo: RULE_2_DEMO }
    ),

    practice(
      "Let's start! This region is three cells tall and needs two stars. Since they can't touch, they have to go at either end.",
      'Tap a cell twice to place a star.',
      STAR,
      () => SHORT_REGION_ENDS
    ),

    practice(
      "Since stars can't touch, the cells around these stars can be eliminated. Mark them with an ✕.",
      'Tap a cell once to place an ✕.',
      X,
      (board) => neighbours(board, SHORT_REGION_ENDS)
    ),

    /*
      The rest of the puzzle, one hint at a time. A hint is kept until the player has done everything it
      asked, so placing the first of three ✕s doesn't swap it for some other hint halfway through; a
      mistake interrupts it, since no hint makes sense on a board that's wrong, and so does clearing a
      mark it was reasoning from, such as the star it says to surround.
    */
    ({ board, cells, complete, previous }) => {
      if (complete) {
        return {
          message: {
            kind: 'complete',
            title: 'Tutorial Complete! 🎉',
            description: "You solved your first Stars puzzle! Now you're ready for a daily one.",
          },
        };
      }

      const wrong = mistakes(board, cells, previous);
      if (wrong.length > 0) {
        return {
          message: {
            kind: 'instruction',
            blocks: [
              wrong.length > 1
                ? "Oops, the highlighted cells don't fit. Tap them to change them."
                : "Oops, the highlighted cell doesn't fit. Tap it to change it.",
            ],
            done: false,
            showNext: false,
          },
          marks: wrong.map((c) => mark(c, MarkedCellType.error)),
        };
      }

      if (previous && hintUnfinished(previous, cells) && evidenceIntact(previous, cells)) return previous;

      const hint = generateTutorialHint(cells, board.regions, board.solution, board.regions.length);
      const step: TutorialStep = {
        message: { kind: 'instruction', blocks: [String(hint.message)], done: false, showNext: false },
        marks: hint.markedCells,
      };
      hintEvidence.set(step, marksOn(cells));
      return step;
    },
  ],
};
